import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface SharedFooterProps {
  showAbout?: boolean;
}

export default function SharedFooter({ showAbout = false }: SharedFooterProps) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/mever', label: 'MeVER' },
    { to: '/mirari-echo', label: 'Echo' },
    { to: '/company', label: 'Company' },
  ];

  const contactLinks = [
    { href: 'mailto:inquiry@bitmoves.jp', label: 'inquiry@bitmoves.jp' },
    { href: 'mailto:pr@bitmoves.jp', label: 'pr@bitmoves.jp' },
  ];

  const externalLinks = [
    {
      href: 'https://prtimes.jp/main/html/searchrlp/company_id/162516',
      label: 'PR TIMES',
    },
    {
      href: 'http://mirari.art',
      label: 'mirari.art',
    },
  ];

  return (
    <footer
      ref={ref}
      className="bg-stone-900 text-white py-20 px-8 lg:px-32"
    >
      <div
        className={`max-w-7xl mx-auto transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {showAbout && (
          <section className="mb-16 pb-16 border-b border-stone-700">
            <h3 className="text-3xl font-medium mb-8">About</h3>
            <div className="max-w-4xl">
              <p className="text-base text-stone-300 leading-relaxed mb-4">
                BITMOVES JAPANは東京を拠点に、プロダクト・体験・実装を横断して&quot;Making Things&quot;
                を行うクリエイティブプロデュースチームです。
              </p>
              <p className="text-sm text-stone-400 leading-relaxed mb-6">
                BITMOVES JAPAN is a Tokyo‑based creative production team working across
                products, experiences, and delivery.
              </p>
              <Link
                to="/company"
                className="inline-flex items-center gap-2 text-sm font-medium text-white hover:text-stone-300 transition-colors cursor-pointer"
              >
                会社概要を見る / View Company Info
                <i className="ri-arrow-right-line text-base w-4 h-4 flex items-center justify-center" />
              </Link>
            </div>
          </section>
        )}

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-16">
          <div>
            <Link to="/" className="cursor-pointer inline-block mb-6">
              <img
                src="https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/ed07e655b2cd85eb693caada04c30b9e.png"
                alt="BITMOVES"
                className="h-7 w-auto object-contain brightness-0 invert"
              />
            </Link>
            <div className="space-y-2 text-sm text-stone-400">
              <p>ビットムーブスジャパン株式会社</p>
              <p>東京都千代田区丸の内1-11-1</p>
              <p>パシフィックセンチュリープレイス丸の内13F</p>
            </div>
          </div>

          <div>
            <h4 className="text-sm uppercase tracking-wider text-stone-500 mb-6">
              Navigation
            </h4>
            <div className="space-y-3 text-sm">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-stone-400 hover:text-white transition-colors duration-300 block cursor-pointer"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm uppercase tracking-wider text-stone-500 mb-6">
              Contact
            </h4>
            <div className="space-y-3 text-sm">
              {contactLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-stone-400 hover:text-white transition-colors duration-300 block cursor-pointer"
                >
                  {link.label}
                </a>
              ))}
              {externalLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-stone-400 hover:text-white transition-colors duration-300 block cursor-pointer"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="pt-8 border-t border-stone-700 flex flex-col lg:flex-row justify-between items-center gap-4 text-sm text-stone-500">
          <p>&copy; 2025 BITMOVES JAPAN. All rights reserved.</p>
          <a
            href="https://readdy.ai/?ref=logo"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="hover:text-stone-300 transition-colors duration-300 cursor-pointer"
          >
            Powered by Readdy
          </a>
        </section>
      </div>
    </footer>
  );
}
